// @ts-nocheck
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Box } from '@mantine/core';
import { useMantineTheme } from '@mantine/core';
import { Stack, Center, Container } from '@/components/ui/layout';
import { Title, Text, Button, Badge } from '@/components/ui/core';
import { PasswordInput } from '@/components/ui/inputs';
import { IconLock, IconArrowLeft, IconLockCheck, IconBolt, IconCheck } from '@tabler/icons-react';
import { FadeIn, SlideIn } from '@/components/animations';
import { NeonCard, PasswordStrength } from '../components';

export default function ResetDarkMode() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isReset, setIsReset] = useState(false);
  const theme = useMantineTheme();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsReset(true);
  };

  const passwordsMatch = password === confirmPassword;
  const canSubmit = password.length >= 8 && passwordsMatch;

  const inputStyles = {
    input: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      borderColor: 'rgba(0, 255, 102, 0.3)',
      color: 'white',
      '&:focus': {
        borderColor: '#00ff66',
        boxShadow: '0 0 10px rgba(0, 255, 102, 0.3)',
      },
    },
    label: { color: '#00ff66' },
  };

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #0a1a0f 50%, #0f0a0a 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Grid background */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            linear-gradient(rgba(0, 255, 102, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 255, 102, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          pointerEvents: 'none',
        }}
      />

      {/* Glow effects */}
      <Box
        style={{
          position: 'absolute',
          top: '15%',
          right: '15%',
          width: 300,
          height: 300,
          background: 'radial-gradient(circle, rgba(0, 255, 102, 0.15) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        style={{
          position: 'absolute',
          bottom: '20%',
          left: '10%',
          width: 250,
          height: 250,
          background: 'radial-gradient(circle, rgba(51, 255, 153, 0.1) 0%, transparent 70%)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Center mih="100vh" p="md" style={{ position: 'relative', zIndex: 1 }}>
        <Container size="xs" w="100%">
          <SlideIn direction="up" delay={100}>
            <NeonCard color="green" animated glass>
              {isReset ? (
                <Stack gap="lg" align="center">
                  <FadeIn delay={100}>
                    <Center
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 20,
                        backgroundColor: 'rgba(0, 255, 102, 0.2)',
                        boxShadow: '0 0 30px rgba(0, 255, 102, 0.3)',
                      }}
                    >
                      <IconCheck size={40} color="#00ff66" />
                    </Center>
                  </FadeIn>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Title order={2} ta="center" c="white">CREDENTIALS UPDATED</Title>
                      <Text c="dimmed" ta="center" maw={300}>
                        New access codes have been configured successfully.
                      </Text>
                    </Stack>
                  </FadeIn>

                  <FadeIn delay={300}>
                    <Button
                      component={Link}
                      to="/app/demo/auth-pages/login/neon"
                      fullWidth
                      size="md"
                      radius="md"
                      color="green"
                      variant="filled"
                      style={{
                        boxShadow: '0 0 20px rgba(0, 255, 102, 0.4)',
                      }}
                    >
                      ACCESS TERMINAL
                    </Button>
                  </FadeIn>
                </Stack>
              ) : (
                <Stack gap="lg">
                  <Button
                    component={Link}
                    to="/app/demo/auth-pages/reset-password"
                    variant="subtle"
                    size="xs"
                    leftSection={<IconArrowLeft size={14} />}
                    w="fit-content"
                    c="green"
                  >
                    Back to variants
                  </Button>

                  <FadeIn delay={200}>
                    <Stack align="center" gap="xs">
                      <Badge
                        leftSection={<IconBolt size={12} />}
                        variant="filled"
                        color="green"
                        size="lg"
                        style={{
                          boxShadow: '0 0 20px rgba(0, 255, 102, 0.5)',
                        }}
                      >
                        Credential Reset
                      </Badge>
                      <Center
                        style={{
                          width: 56,
                          height: 56,
                          borderRadius: 14,
                          backgroundColor: 'rgba(0, 255, 102, 0.2)',
                          marginTop: 'var(--mantine-spacing-sm)',
                        }}
                      >
                        <IconLockCheck size={28} color="#00ff66" />
                      </Center>
                      <Title order={2} ta="center" mt="sm" c="white">
                        NEW CREDENTIALS
                      </Title>
                      <Text size="sm" ta="center" c="dimmed" maw={300}>
                        Configure new access codes for your identity
                      </Text>
                    </Stack>
                  </FadeIn>

                  <form onSubmit={handleSubmit}>
                    <Stack gap="md">
                      <FadeIn delay={300}>
                        <div>
                          <PasswordInput
                            label="New Password"
                            placeholder="••••••••"
                            leftSection={<IconLock size={16} />}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            size="md"
                            radius="md"
                            styles={inputStyles}
                          />
                          {password && <PasswordStrength password={password} variant="bar" />}
                        </div>
                      </FadeIn>

                      <FadeIn delay={400}>
                        <PasswordInput
                          label="Confirm Password"
                          placeholder="••••••••"
                          leftSection={<IconLock size={16} />}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          size="md"
                          radius="md"
                          styles={inputStyles}
                          error={confirmPassword && !passwordsMatch ? "Mismatch detected" : undefined}
                        />
                      </FadeIn>

                      <FadeIn delay={500}>
                        <Button
                          type="submit"
                          fullWidth
                          size="md"
                          radius="md"
                          color="green"
                          variant="filled"
                          disabled={!canSubmit}
                          style={{
                            boxShadow: '0 0 20px rgba(0, 255, 102, 0.4)',
                          }}
                        >
                          UPDATE CREDENTIALS
                        </Button>
                      </FadeIn>
                    </Stack>
                  </form>

                  <FadeIn delay={600}>
                    <Center>
                      <Button
                        component={Link}
                        to="/app/demo/auth-pages/login/neon"
                        variant="subtle"
                        c="green"
                        leftSection={<IconArrowLeft size={14} />}
                      >
                        Return to terminal
                      </Button>
                    </Center>
                  </FadeIn>
                </Stack>
              )}
            </NeonCard>
          </SlideIn>
        </Container>
      </Center>
    </Box>
  );
}
